/**
 * PDP Mixed Cart Guard (clean naming, no selector helpers)
 * - Blocks mixing one-time and subscription products.
 * - Uses a modal (#MixedCartModal) to let the user cancel or switch to subscription.
 * - Honors the __mixedCartBypassOnce flag (consumed by product-form.js).
 */
document.addEventListener('DOMContentLoaded', () => {
  // Must remain this exact string (external dependency)
  const FORM_BYPASS_FLAG = '__mixedCartBypassOnce';

  let isAddToCartNetworkBlocked = false;
  let allowSingleAddToCartThrough = false;

  // ----------------- Purchase option helpers -----------------
  const extractFrequency = (str) => String(str || '').match(/\d+/)?.[0] || '';

  const getSelectedPurchaseOption = () => {
    const checked = document
      .querySelector('recharge-subscription-widget')
      ?.shadowRoot?.querySelector('[name="purchaseOption"]:checked');
    if (!checked) return null;
    return checked.value === 'onetime' ? 'onetime' : 'subscription';
  };

  const selectPurchaseOption = async (target /* 'subscription' | 'onetime' */) => {
    const radios = document
      .querySelector('recharge-subscription-widget')
      ?.shadowRoot?.querySelectorAll('[name="purchaseOption"]');
    const selectedPlan = extractFrequency(
      window?.Rebuy?.SmartCart?.cart?.items[0]?.selling_plan_allocation?.selling_plan?.name,
    );
    if (!radios?.length) return false;
    for (const radio of radios) {
      const val = radio.value === 'onetime' ? 'onetime' : 'subscription';
      if (val === target) {
        radio.click();
        if (selectedPlan) {
          await selectSellingPlanByName(selectedPlan);
        }
        return true;
      }
    }
    return false;
  };

  const waitForRechargeRoot = async ({ timeout = 5000, interval = 100 } = {}) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const root = document
        .querySelector('recharge-subscription-widget')
        ?.shadowRoot.querySelector('rc-selling-plans')?.shadowRoot;
      if (root) return root;
      await new Promise((r) => setTimeout(r, interval));
    }
    return null;
  };

  const selectSellingPlanByName = async (freqOrName) => {
    const root = await waitForRechargeRoot();
    if (!root) return false;
    const targetFreq = extractFrequency(freqOrName) || String(freqOrName || '').trim();
    for (const planInput of root.querySelectorAll('[name="rc_plan"]')) {
      const label = planInput.getAttribute('aria-label') || '';
      if (
        (targetFreq && extractFrequency(label) === targetFreq) ||
        (targetFreq &&
          extractFrequency(planInput.parentElement?.querySelector('.rc-plans-button__interval')?.textContent) ===
            targetFreq) ||
        (label && label.startsWith(String(freqOrName)))
      ) {
        planInput.click();
        return true;
      }
    }
    return false;
  };

  // ----------------- Cart state -----------------
  const getCartState = () => {
    const cart = window.__CART_STATE;
    return cart && Array.isArray(cart.items) ? cart : { items: [] };
  };

  const computeCartMixFlags = (cart) => {
    let hasSubscriptionItems = false;
    let hasOneTimeItems = false;
    for (const item of cart.items) {
      if (item.selling_plan_allocation) hasSubscriptionItems = true;
      else hasOneTimeItems = true;
      if (hasSubscriptionItems && hasOneTimeItems) break;
    }
    return { hasSubscriptionItems, hasOneTimeItems };
  };

  // ----------------- Global block flag -----------------
  const setNetworkAddToCartBlocked = (blocked) => {
    isAddToCartNetworkBlocked = !!blocked;
    window.__MixedCart_BlockAdd = isAddToCartNetworkBlocked;
  };

  // ----------------- Form submit -----------------
  const submitProductForm = (form) => {
    form[FORM_BYPASS_FLAG] = true;
    form.__mixedCartGuardBusy = false;

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.click();
    }
  };

  const waitForCartItem = async (variantId, { timeout = 4000, interval = 75 } = {}) => {
    const start = Date.now();

    const hasSubscriptionAttribution = (props) =>
      props && typeof props._attribution === 'string' && props._attribution.toLowerCase().includes('subscription');

    while (Date.now() - start < timeout) {
      const cart = window?.Rebuy?.SmartCart?.cart;
      if (cart && Array.isArray(cart.items)) {
        const item = cart.items.find((i) => i.variant_id === variantId);
        if (item && Object.entries(item.properties).length && hasSubscriptionAttribution(item.properties)) return item;
      }
      await new Promise((r) => setTimeout(r, interval));
    }
    return null; // timed out
  };

  const updateProductProperties = async (form, waitForSync = false) => {
    const variantId = parseInt(form.querySelector('[name="id"]')?.value || '', 10);
    let currentProduct = window.Rebuy.SmartCart.cart.items.find((item) => item.variant_id === variantId);
    if (currentProduct) {
      if (waitForSync) {
        currentProduct = await waitForCartItem(variantId);
      }
      for (const [key, val] of Object.entries(currentProduct.properties)) {
        if (val == null) continue;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = typeof val === 'object' ? JSON.stringify(val) : String(val);
        form.appendChild(input);
      }
    }

    return form;
  };

  // ----------------- Modal -----------------
  const openMixedCartModal = ({ conflictType, onCancel, onConfirmSwitch, openerElement, override }) => {
    // override: { title, message, switchText, cancelText }
    const modal = document.getElementById('MixedCartModal');
    const cancelButton = modal?.querySelector('[data-mixed-cancel]');
    const switchButton = modal?.querySelector('[data-mixed-switch]');
    if (!modal?.show || !cancelButton || !switchButton) {
      onCancel?.();
      return;
    }

    const titleEl = modal.querySelector('#MixedCartModalTitle');
    const messageEl = modal.querySelector('#MixedCartModalMessage');

    const updateTextContent = (title, message, switchText, cancelText) => {
      if (!titleEl || !messageEl) return;
      titleEl.textContent = title;
      messageEl.innerHTML = message;
      switchButton.textContent = switchText;
      cancelButton.textContent = cancelText;
    };

    // Allow overriding modal content (e.g., subscription frequency mismatch)
    if (override?.title || override?.message) {
      updateTextContent(
        override.title || '',
        override.message || '',
        override.switchText || switchButton.textContent,
        override.cancelText || cancelButton.textContent,
      );
    } else if (conflictType === 'cart-has-sub_current-onetime') {
      updateTextContent(
        window.mixSubscribeCart.heading,
        window.mixSubscribeCart.description,
        window.mixSubscribeCart.add_button,
        window.mixSubscribeCart.cancel_button,
      );
    } else {
      updateTextContent(
        window.mixOneTimeCart.heading,
        window.mixOneTimeCart.description,
        window.mixOneTimeCart.add_button,
        window.mixOneTimeCart.cancel_button,
      );
    }

    let finished = false;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      cancelButton.removeEventListener('click', onCancelClick);
      switchButton.removeEventListener('click', onSwitchClick);
      document.body.removeEventListener('modalClosed', onModalClosed);
    };

    const onCancelClick = () => {
      cleanup();
      modal.hide?.();
      onCancel?.();
    };
    const onSwitchClick = () => {
      cleanup();
      modal.hide?.();
      onConfirmSwitch?.();
    };
    const onModalClosed = () => {
      cleanup();
      onCancel?.();
    };

    cancelButton.addEventListener('click', onCancelClick, { once: true });
    switchButton.addEventListener('click', onSwitchClick, { once: true });
    document.body.addEventListener('modalClosed', onModalClosed, { once: true });

    try {
      modal.show(openerElement || document.activeElement || null);
    } catch {
      onCancel?.();
    }
  };

  // ----------------- Network guard -----------------
  const installAddToCartNetworkGuard = () => {
    if (!window.fetch || window.__mixedCartFetchWrapped) return;

    const originalFetch = window.fetch.bind(window);
    const isAddToCartRequest = (input) => {
      const url = typeof input === 'string' ? input : input?.url;
      return typeof url === 'string' && url.includes('/cart/add');
    };

    window.fetch = async (input, init) => {
      if (isAddToCartNetworkBlocked && isAddToCartRequest(input)) {
        if (allowSingleAddToCartThrough) {
          allowSingleAddToCartThrough = false;
        } else {
          return new Response(
            JSON.stringify({
              status: 422,
              message: 'Mixed cart not allowed',
              description: 'Your cart cannot contain both one-time purchases and subscription products.',
            }),
            { status: 422, headers: { 'Content-Type': 'application/json' } },
          );
        }
      }
      return originalFetch(input, init);
    };
    window.__mixedCartFetchWrapped = true;
  };

  // Remove one-time line for same variant when switching to subscription
  const removeOneTimeLineForVariant = async (variantId) => {
    const cart = getCartState();
    const itemIndex = cart.items.findIndex(
      (it) => !it.selling_plan_allocation && (it.variant_id === variantId || it.id === variantId),
    );
    if (itemIndex === -1) return null;

    const item = cart.items[itemIndex] || {};
    const payload = item.key ? { id: item.key, quantity: 0 } : { line: itemIndex + 1, quantity: 0 };

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;

    const nextCart = await res.json();
    if (nextCart?.items) window.__CART_STATE = nextCart;
    return nextCart;
  };

  // Remove one-time line for same variant when switching to subscription
  const clearCartAndRebuildWithSubscription = async (variantId) => {
    const cart = window.Rebuy.SmartCart.cart;

    const clearRes = await fetch('/cart/clear.js', { method: 'POST' });
    if (!clearRes.ok) return null;

    let items = [];
    const selectedSubscriptionFreq = extractFrequency(
      document
        .querySelector('recharge-subscription-widget')
        ?.shadowRoot.querySelector('rc-selling-plans')
        ?.shadowRoot.querySelector('[name="rc_plan"]:checked')
        ?.parentElement.querySelector('.rc-plans-button__interval')?.textContent,
    );
    cart.items.forEach((item) => {
      if (item.variant_id === variantId) {
        item.quantity += Number(document.querySelector('product-info .quantity__input').value);
      }
      if (item.product.selected_variant.selling_plans) {
        const matchingPlan = item.product.selected_variant.selling_plans.find(
          (plan) => extractFrequency(plan.name) === selectedSubscriptionFreq,
        );
        if (matchingPlan) {
          items.push({
            id: Number(item.variant_id),
            quantity: Number(item.quantity),
            selling_plan: Number(matchingPlan.id),
          });
        }
      }
    });

    if (items.findIndex((item) => item.id === variantId) === -1) {
      items.push({
        id: variantId,
        quantity: Number(document.querySelector('product-info .quantity__input').value),
        selling_plan: Number(
          document
            .querySelector('recharge-subscription-widget')
            ?.shadowRoot.querySelector('rc-selling-plans')
            .shadowRoot.querySelector('[name="rc_plan"]:checked').value,
        ),
      });
    }

    const res = await fetch('/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) return null;

    const nextCart = await res.json();
    if (nextCart?.items) window.__CART_STATE = nextCart;
    return nextCart;
  };

  // ----------------- Form guard -----------------
  const attachGuardToProductForm = (form) => {
    if (!form || form.__mixedCartGuardAttached) return;
    form.__mixedCartGuardAttached = true;
    form.__mixedCartGuardBusy = false;

    const onAttemptAddToCart = async (evt) => {
      if (form.__mixedCartGuardBusy) {
        evt?.preventDefault?.();
        evt?.stopImmediatePropagation?.();
        evt?.stopPropagation?.();
        return;
      }

      // Allow a single pass-through when we intentionally resubmit
      if (form[FORM_BYPASS_FLAG]) {
        if (evt?.type === 'submit') delete form[FORM_BYPASS_FLAG];
        return;
      }

      evt?.preventDefault?.();
      evt?.stopImmediatePropagation?.();
      evt?.stopPropagation?.();
      form.__mixedCartGuardBusy = true;

      const selection = getSelectedPurchaseOption() || 'onetime';
      const { hasSubscriptionItems, hasOneTimeItems } = computeCartMixFlags(getCartState());
      const isConflict =
        (selection === 'subscription' && hasOneTimeItems) || (selection === 'onetime' && hasSubscriptionItems);

      if (!isConflict) {
        // Additional guard: if cart already has subscription items, ensure selling plan frequency matches
        if (selection === 'subscription' && hasSubscriptionItems) {
          const cart = getCartState();
          const cartSubItem = cart.items.find((it) => !!it.selling_plan_allocation);
          const cartPlanId = cartSubItem?.selling_plan_allocation?.selling_plan?.id;
          const cartPlanName = cartSubItem?.selling_plan_allocation?.selling_plan?.name;

          const selectedPlanName = document
            .querySelector('recharge-subscription-widget')
            ?.shadowRoot.querySelector('rc-selling-plans')
            ?.shadowRoot?.querySelector('[name="rc_plan"]:checked')
            ?.parentElement.querySelector('.rc-plans-button__interval')
            ?.textContent?.trim();

          const cartFreq = extractFrequency(cartPlanName);
          const selectedFreq = extractFrequency(selectedPlanName);

          if (cartFreq && selectedFreq && cartFreq !== selectedFreq) {
            // Build friendly labels
            const widget = document
              .querySelector('recharge-subscription-widget')
              ?.shadowRoot.querySelector('rc-selling-plans')?.shadowRoot;
            const selectedPlanInput = widget?.querySelector('[name="rc_plan"]:checked');
            const selectedLabelText =
              selectedPlanInput?.parentElement.querySelector('.rc-plans-button__interval')?.textContent?.trim() || '';

            setNetworkAddToCartBlocked(true);
            openMixedCartModal({
              conflictType: 'subscription-frequency-mismatch',
              openerElement: form.querySelector('[type="submit"]') || form,
              override: {
                title: window.multiSubscribeCart.heading,
                message: window.multiSubscribeCart.description
                  .replace('{{ current }}', cartFreq)
                  .replace('{{ selected }}', selectedFreq),
                switchText: window.multiSubscribeCart.add_button.replace('{{ current }}', cartFreq),
                cancelText: window.multiSubscribeCart.cancel_button.replace('{{ selected }}', selectedFreq),
              },
              onCancel: async () => {
                window?.Rebuy?.SmartCart?.cart?.items.forEach((item) => {
                  const subscriptionName = item?.product?.selected_variant?.selling_plans?.find(
                    (plan) => extractFrequency(plan.name) === selectedFreq,
                  )?.name;
                  if (subscriptionName) {
                    window?.Rebuy?.SmartCart?.updateItemDeliveryFrequency(item, subscriptionName, 'onetime');
                  }
                });
                form = await updateProductProperties(form, true);

                // Proceed with selected subscription
                allowSingleAddToCartThrough = true;
                setNetworkAddToCartBlocked(false);
                submitProductForm(form);
              },
              onConfirmSwitch: async () => {
                if (!(await selectSellingPlanByName(cartFreq))) {
                  form.__mixedCartGuardBusy = false;
                  return;
                }
                // Proceed with selected subscription
                allowSingleAddToCartThrough = true;
                setNetworkAddToCartBlocked(false);
                submitProductForm(form);
              },
            });
            return; // stop here; modal will handle flow
          }
        }

        submitProductForm(form);
        return;
      }

      const conflictType = selection === 'subscription' ? 'cart-has-one_current-sub' : 'cart-has-sub_current-onetime';

      setNetworkAddToCartBlocked(true);

      openMixedCartModal({
        conflictType,
        openerElement: form.querySelector('[type="submit"]') || form,
        onCancel: () => {
          setNetworkAddToCartBlocked(false);
          form.__mixedCartGuardBusy = false;
        },
        onConfirmSwitch: async () => {
          if (selection === 'subscription') {
            const variantId = parseInt(form.querySelector('[name="id"]')?.value || '', 10);

            form[FORM_BYPASS_FLAG] = true;
            form.__mixedCartGuardBusy = false;
            allowSingleAddToCartThrough = true;
            setNetworkAddToCartBlocked(false);
            await clearCartAndRebuildWithSubscription(variantId);
            form[FORM_BYPASS_FLAG] = false;
          } else {
            const switched = await selectPurchaseOption('subscription');
            if (!switched) {
              form.__mixedCartGuardBusy = false;
              return;
            }

            form = await updateProductProperties(form, false);

            allowSingleAddToCartThrough = true;
            setNetworkAddToCartBlocked(false);
            submitProductForm(form);
          }
        },
      });
    };

    form.addEventListener('submit', onAttemptAddToCart, true);
    form.querySelector('[type="submit"]')?.addEventListener('click', onAttemptAddToCart, true);
  };

  // ----------------- Init -----------------
  const initMixedCartGuard = () => {
    setNetworkAddToCartBlocked(false);
    installAddToCartNetworkGuard();

    // Attach to existing product forms
    document.querySelectorAll('product-form form').forEach(attachGuardToProductForm);

    // Keep window.__CART_STATE in sync with theme pub/sub
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (evt) => {
        const nextCart = evt?.data?.cartData;
        if (nextCart) window.__CART_STATE = nextCart;
      });
    }

    // Hard-block native /cart/add submits while modal is active
    document.addEventListener(
      'submit',
      (e) => {
        if (!window.__MixedCart_BlockAdd) return;
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if ((form.getAttribute('action') || '').toLowerCase().includes('/cart/add')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();
        }
      },
      true,
    );
  };

  initMixedCartGuard();
});
